import { Link, useNavigate } from "react-router";
import { useAppState } from "../context";
import type { UserType, NavigationType } from "../types";
import { NAVIGATION_GROUP } from "../constants/navigation";
import useCart from "../pages/cart/useCart.ts";
import useCatalogFilterParams from "../pages/catalog/useCatalogFilterParams.ts";
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { useEffect, useState, FormEvent } from "react";
import type { Swiper as SwiperType } from 'swiper';
import { FreeMode } from 'swiper/modules';
import EditableTextBlock from "../utils/editable-text-block.tsx";

const MEDIA_MD = 768; // Define your breakpoint here

export const Header = () => {
    const navigate = useNavigate();
    const { setFilters } = useCatalogFilterParams();
    const [searchValue, setSearchValue] = useState('');
    const [user, setUser] = useAppState<UserType | undefined>('user');
    const [navigation] = useAppState<NavigationType[]>('navigation', []);
    const { itemCount: cartItemCount } = useCart();
    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < MEDIA_MD);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        const trimmedValue = searchValue.trim();
        if (trimmedValue) {
            setFilters({'keyword' : trimmedValue});
            navigate({
                pathname: '/catalog',
            });
            setSearchValue(''); // Clear the search input after search
        }
    };

    useEffect(() => {
        const breakpoint = window.matchMedia(`(max-width:${MEDIA_MD - 0.02}px)`);

        const handleBreakpointChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setIsMobile(e.matches);
            if (e.matches && swiperInstance) {
                swiperInstance.destroy(true, true);
                setSwiperInstance(null);
            }
        };

        breakpoint.addEventListener('change', handleBreakpointChange);
        handleBreakpointChange(breakpoint);

        return () => {
            breakpoint.removeEventListener('change', handleBreakpointChange);
        };
    }, [swiperInstance]);

    const logout = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        ev.preventDefault();
        fetch(ev.currentTarget.href)
            .then(() => {
                setUser(undefined);
                navigate(0);

            });
    };

    const headerNavItems = navigation
        .filter(item => item.group === NAVIGATION_GROUP.HEADER)
        .sort((a, b) => a.position - b.position);

    return <header className="header">
        <div className="header__top">
            <div className="inside-wrapper">
                <div className="header__inner">
                    <div className="header__logo">
                        <Link to="/" className="header__logo-holder">
                            <img className="header__logo-img" src="./img/slidy.svg" alt="Slidy" width="109" height="58" />
                        </Link>
                        <div className="header__logo-text">
                            <EditableTextBlock definedName="header-logo-text" title="Site name">
                                The best story is&nbsp;your story
                            </EditableTextBlock>
                        </div>
                    </div>
                    <div className="header__searchbox">
                        <div className="searchbox">
                            <form className="searchbox__form" onSubmit={handleSearch}>
                                <input
                                    required
                                    type="text"
                                    className="searchbox__input"
                                    placeholder="search"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                                <button className="searchbox__btn btn btn_cta btn_sm" type="submit" aria-label="Search">
                                    <svg role="img" className="btn__ico btn__ico_fill">
                                        <use href="./img/sprite.svg#search"></use>
                                    </svg>
                                </button>
                            </form>
                        </div>
                    </div>
                    <div className="header__account haccount">
                        <ul className="haccount__list">
                            {user &&
                                <>
                                    <li className="haccount__item">
                                        <Link to="/account" className="haccount__link">
                                            <svg role="img" className="haccount__ico">
                                                <use href="./img/sprite.svg#h_user"></use>
                                            </svg>
                                            <span className="haccount__link-name">
                                                Account
                                            </span>
                                        </Link>
                                    </li>
                                    <li className="haccount__item">
                                        <Link to="/auth/logout" className="haccount__link" onClick={logout}>
                                            <svg
                                                fill="none"
                                                className="haccount__ico"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            ><g stroke="#141414" strokeLinecap="round" strokeLinejoin="round"
                                                strokeWidth="1.5"><path d="m15.75 8.75 3.5 3.25-3.5 3.25" /><path d="m19 12h-8.25" /><path d="m15.25 4.75h-8.5c-1.10457 0-2 .89543-2 2v10.5c0 1.1046.89543 2 2 2h8.5" /></g></svg>
                                            <span className="haccount__link-name">
                                            Sign Out
                                            </span>
                                        </Link>
                                    </li>
                                    <li className="haccount__item">
                                        <Link to="/account/wishlist" className="haccount__link">
                                            <svg role="img" className="haccount__ico">
                                                <use href="./img/sprite.svg#h_heart"></use>
                                            </svg>
                                            <span className="haccount__link-name">
                                                Wishlist
                                            </span>
                                        </Link>
                                    </li>
                                </>}
                            {!user && <>
                                <li className="haccount__item">
                                    <Link to="/sign-in" className="haccount__link">
                                        <svg role="img" className="haccount__ico">
                                            <use href="./img/sprite.svg#h_user"></use>
                                        </svg>
                                        <span className="haccount__link-name">
                                            Sign In
                                        </span>
                                    </Link>
                                </li>
                                <li className="haccount__item">
                                    <Link to="/sign-up" className="haccount__link">
                                        <svg role="img" className="haccount__ico">
                                            <use href="./img/sprite.svg#h_lock"></use>
                                        </svg>
                                        <span className="haccount__link-name">
                                            Sign Up
                                        </span>
                                    </Link>
                                </li>
                                <li className="haccount__item">
                                    <Link to="/wishlist" className="haccount__link">
                                        <svg role="img" className="haccount__ico">
                                            <use href="./img/sprite.svg#h_heart"></use>
                                        </svg>
                                        <span className="haccount__link-name">
                                            Wishlist
                                        </span>
                                    </Link>
                                </li>

                            </>}
                        </ul>
                    </div>

                </div>
            </div>
        </div>
        <div className="header__bottom">
            <div className="inside-wrapper">
                <div className="header__inner">
                    <nav className={`header__nav nav  ${isMenuOpen ? 'm-active' : ''}`}>
                        <button className={`nav__open js_toggle_trigger`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
                            <svg role="img" className="nav__open-ico">
                                <use href="./img/sprite.svg#burger"></use>
                            </svg>
                            MENU
                        </button>
                        <Swiper
                            modules={[FreeMode]}
                            slidesPerView={'auto'}
                            spaceBetween={0}
                            resistanceRatio={0}
                            enabled={!isMobile}
                            onSwiper={setSwiperInstance}
                            injectStylesUrls={[]}
                            freeMode={{
                                enabled: true,
                                sticky: false
                            }}
                            className="nav__slider navslider swiper js_swiper_nav">
                            <button className="nav__close" aria-label="Close menu" onClick={() => setIsMenuOpen(false)}>
                                <svg role="img" className="nav__close-ico">
                                    <use href="./img/sprite.svg#cross"></use>
                                </svg>
                                MENU
                            </button>
                            <>
                                {headerNavItems.map((item, index) => (
                                    <SwiperSlide className="nav__item swiper-slide" key={index}>
                                        <Link to={item.path} className="nav__link" onClick={() => setIsMenuOpen(false)}>{item.title}</Link>
                                    </SwiperSlide>
                                ))}
                            </>
                        </Swiper>
                    </nav>
                    {/*</header__nav-->*/}
                    <div className="header__cart">
                        <Link to={`${user ? '/account/cart' : '/cart'}`} className="mycart">
                            <svg role="img" className="mycart__ico">
                                <use href="./img/sprite.svg#mycart"></use>
                            </svg>
                            {cartItemCount > 0 && <span className="mycart__qu">{cartItemCount}</span>}
                            <span className="mycart__label">My cart</span>
                        </Link>
                    </div>
                    {/*<!--/header__cart-->*/}
                </div>
            </div>
        </div>
    </header>
}